import { FavoritesService } from "@gtmhub/favorites/favorites.service";
import { IFavoriteItemsGroupedReqParams } from "@gtmhub/favorites/models";
import { ICollection } from "@gtmhub/models";
import { UXCustomizationType } from "@gtmhub/uxcustomization/models";
import { IViewHistoryItemsGroupedReqParams, ViewHistoryService } from "@gtmhub/view-history";
import { INavItem, NavItemsType } from "@webapp/navigation/models/nav-items-list.models";
import { favoritesGroupedReponseToNavItems, viewHistoryGroupedReponseToNavItems } from "@webapp/navigation/utils/nav-items.util";
import { from, Subscription } from "rxjs";
import { FavoritesFactory } from "../favorites/favorites-factory";
import { ViewHistoryFactory } from "../view-history/view-history-factory";

export class NavigationItemsFacade {
  private entityTypes: UXCustomizationType[];
  private entityNavType: NavItemsType;
  private recentSubscription$: Subscription;
  private viewHistoryChangesSubscription$: Subscription;
  private viewHistoryPatchSubscription$: Subscription;
  private favoritesSubscription$: Subscription;
  private favoritesChangesSubscription$: Subscription;

  private recent: ICollection<INavItem>;
  private favorites: ICollection<INavItem>;

  public static $inject = ["ViewHistoryService", "ViewHistoryFactory", "FavoritesService", "FavoritesFactory"];

  constructor(
    private viewHistoryService: ViewHistoryService,
    private viewHistoryFactory: ViewHistoryFactory,
    private favoritesService: FavoritesService,
    private favoritesFactory: FavoritesFactory
  ) {}

  private getRecent() {
    const recentItemsRequestParams: IViewHistoryItemsGroupedReqParams = this.viewHistoryFactory.buildNavigationViewHistoryRequestParams(this.entityTypes);
    const promise = this.viewHistoryService
      .getViewHistoryItems(recentItemsRequestParams)
      .then((collection) => viewHistoryGroupedReponseToNavItems(collection, this.entityNavType));

    return from(promise);
  }

  private getFavorites() {
    const favoritesRequestParams: IFavoriteItemsGroupedReqParams = this.favoritesFactory.buildNavigationFavoritesRequestParams(this.entityTypes);
    const promise = this.favoritesService
      .getFavoriteItems(favoritesRequestParams)
      .then((collection) => favoritesGroupedReponseToNavItems(collection, this.entityNavType));

    return from(promise);
  }

  public setupRecents() {
    this.recentSubscription$ = this.getRecent().subscribe((collection) => (this.recent = collection));
    const viewHistoryChangeHandler = this.viewHistoryFactory.buildRecentChangeHandler(this.entityTypes, "navigation");
    this.viewHistoryChangesSubscription$ = this.viewHistoryService.viewHistoryChangeSubject.subscribe((changeEvent) => {
      this.recent = viewHistoryChangeHandler(changeEvent, this.recent);
    });
    const viewHistoryPatchHandler = this.viewHistoryFactory.buildRecentPatchHandler(this.entityTypes, "navigation");
    this.viewHistoryPatchSubscription$ = this.viewHistoryService.viewHistoryPatchSubject.subscribe((changeEvent) => {
      this.recent = viewHistoryPatchHandler(changeEvent, this.recent);
    });
  }

  public setupFavorites() {
    this.favoritesSubscription$ = this.getFavorites().subscribe((collection) => (this.favorites = collection));
    const favoritesChangeHandler = this.favoritesFactory.buildFavoritesChangeHandler(this.entityTypes);
    this.favoritesChangesSubscription$ = this.favoritesService.favoritesChangeSubject.subscribe((changeEvent) => {
      this.favorites = favoritesChangeHandler(changeEvent, this.favorites);
    });
  }

  public unsubscribeRecents() {
    if (this.recentSubscription$) {
      this.recentSubscription$.unsubscribe();
    }
    if (this.viewHistoryChangesSubscription$) {
      this.viewHistoryChangesSubscription$.unsubscribe();
    }
    if (this.viewHistoryPatchSubscription$) {
      this.viewHistoryPatchSubscription$.unsubscribe();
    }
    if (this.favoritesSubscription$) {
      this.favoritesSubscription$.unsubscribe();
    }
    if (this.favoritesChangesSubscription$) {
      this.favoritesChangesSubscription$.unsubscribe();
    }
  }

  public setupEntityTypes(entityTypes: UXCustomizationType[], entityNavType: NavItemsType) {
    this.entityTypes = entityTypes;
    this.entityNavType = entityNavType;
  }

  public getRecentItems(): ICollection<INavItem> {
    return this.recent;
  }

  public getFavoriteItems(): ICollection<INavItem> {
    return this.favorites;
  }

  public markItemAsFavorite(id: string, type: UXCustomizationType, navItem: INavItem) {
    const payload = this.favoritesService.favoritesPayloadBuilder.forType(type).withItem({ id }).generate();
    this.favoritesService.markItemAsFavorite(payload, navItem);
  }

  public unmarkItemAsFavorite(id: string, navItem: INavItem) {
    this.favoritesService.unmarkItemAsFavorite(id, navItem);
  }
}
